import { Modal, ModalBody, ModalFoot, ModalHead } from '@dixa/lib-shared-habla';
import type { Team } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { FormEvent } from 'react';

import { TeamsSelect } from 'fable/teamsSelect/containers/teamsSelect';
import { withSelection, WithSelectionInjectedProps } from 'hocs';
import { useNotifications } from 'hooks/useNotifications';

import styles from './teamsAddModal.module.scss';

export interface TeamsAddModalProps extends WithSelectionInjectedProps<Team> {
  isOpen: boolean;
  onClose: (canceled?: boolean) => void;
  addTeams: (teamIds: string[]) => Promise<unknown>;
  queueId?: string;
}

const TeamsAddModalImpl = (props: TeamsAddModalProps): JSX.Element => {
  const notify = useNotifications();

  const getTeamsPayload = (): string[] => {
    return props.selection.selectedItems.map((value) => value.id);
  };

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    const { selection } = props;

    if (!selection.selectedItems.length) {
      return;
    }

    const { addTeams } = props;

    addTeams(getTeamsPayload())
      .then(() => {
        ampli.settingsQueueTeamsAddTeamFormSubmitted();

        const notificationOptions = {
          title: `Team added`,
          text: `Team has been added`,
          icon: 'i-done',
          className: 'success',
          time: 5000,
        };

        notify(notificationOptions);
        handleClose();
      })
      .catch((e) => {
        const notificationOptions = {
          title: 'An error occurred',
          text: `An error occurred when trying to add team`,
          icon: 'i-exclamation',
          className: 'danger',
        };

        notify(notificationOptions);
        throw new Error(`Failed to add team`);
      });
  };

  const handleClose = (canceled = false) => {
    const { selection, onClose } = props;

    selection.setSelectedItems([]);
    onClose(canceled);
  };

  const handleCloseCanceled = () => {
    handleClose(true);
  };

  const { isOpen, selection, queueId } = props;

  return (
    <Modal isOpen={isOpen} onClose={handleCloseCanceled}>
      <ModalHead title={`Add team(s) to queue`} onClose={handleCloseCanceled} />
      <form onSubmit={handleSubmit}>
        <ModalBody className={styles.teamsAddModalBody}>
          <TeamsSelect
            selectedItems={selection.selectedItems}
            selectTeam={selection.selectItem}
            deselectTeam={selection.deselectItem}
            queueId={queueId}
          />
        </ModalBody>
        <ModalFoot
          cancelOnClick={handleCloseCanceled}
          cancelName="Cancel"
          confirmOnClick={handleSubmit}
          confirmName="Add team(s)"
          confirmProps={{
            disabled: !selection.selectedItems.length,
          }}
        />
      </form>
    </Modal>
  );
};

export const TeamsAddModal = withSelection<TeamsAddModalProps, Team>(
  TeamsAddModalImpl,
);
